import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Share2, BookOpen, Copy, Check, Mail, Pencil, X } from "lucide-react";
import api from "../lib/api";
import { CHAPTERS, FAMILY_QUOTES } from "../data/content";
import { Ornament } from "../components/Ornament";
import { Monogram } from "../components/Monogram";
import { AudioPlayer } from "../components/AudioPlayer";
import StoryReactions from "../components/StoryReactions";

function PhotoImg({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        u = URL.createObjectURL(res.data);
        setUrl(u);
      } catch (e) {}
    })();
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [path]);
  if (!url) return null;
  return <img src={url} alt="" className="w-full max-h-[460px] object-cover mb-8" style={{ borderRadius: 4 }} data-testid="story-photo" />;
}

export default function StoriesFeed() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareStory, setShareStory] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [plist, slist] = await Promise.all([
          api.get("/profiles"),
          api.get(`/stories/${profileId}`),
        ]);
        const me = (plist.data || []).find((p) => p.profile_id === profileId);
        if (!me) { navigate("/dashboard"); return; }
        setProfile(me);
        setStories(slist.data || []);
      } catch (e) {
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [profileId, navigate]);

  const beginEdit = (s) => {
    setEditingId(s.story_id);
    setEditDraft(s.answer_text || "");
    setEditError("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
    setEditError("");
  };
  const saveEdit = async (storyId) => {
    setEditSaving(true); setEditError("");
    try {
      const res = await api.patch(`/stories/${storyId}`, { answer_text: editDraft });
      setStories((all) => all.map((s) => s.story_id === storyId ? { ...s, ...res.data } : s));
      cancelEdit();
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Couldn't save just now. Try again?");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading || !profile) return (
    <div className="min-h-screen flex items-center justify-center paper">
      <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>Reflecting on your story…</p>
    </div>
  );

  const filled = stories.filter((s) => (s.answer_text || "").trim().length > 0);

  const grouped = CHAPTERS.map((ch) => ({
    chapter: ch,
    items: filled.filter((s) => s.chapter_id === ch.id).sort((a, b) => a.prompt_index - b.prompt_index),
  })).filter((g) => g.items.length > 0);

  // Insert family quotes between every 3rd story
  const flatList = [];
  let counter = 0;
  for (const g of grouped) {
    flatList.push({ type: "chapter-header", chapter: g.chapter });
    for (const s of g.items) {
      flatList.push({ type: "story", story: s, chapter: g.chapter });
      counter++;
      if (counter % 3 === 0) {
        flatList.push({ type: "quote", quote: FAMILY_QUOTES[(counter / 3 - 1) % FAMILY_QUOTES.length] });
      }
    }
  }

  const buildShareText = (story, chapter) => {
    const prompt = chapter.prompts[story.prompt_index];
    const parts = [
      `${chapter.title}`,
      `— ${prompt}`,
      "",
      story.answer_text || "",
    ];
    if (story.followup_question && story.followup_answer) {
      parts.push("", `— ${story.followup_question}`, "", story.followup_answer);
    }
    parts.push("", `— ${profile.name}`, "From The Living Book");
    return parts.join("\n");
  };

  const copyShare = async () => {
    if (!shareStory) return;
    try { await navigator.clipboard.writeText(buildShareText(shareStory.story, shareStory.chapter)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) {}
  };
  const emailShare = () => {
    if (!shareStory) return;
    const body = encodeURIComponent(buildShareText(shareStory.story, shareStory.chapter));
    const subject = encodeURIComponent(`A story from ${profile.name}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen paper" data-testid="stories-feed-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to={`/app/profile/${profileId}`} className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> Chapters
        </Link>
        <button onClick={() => navigate(`/app/book/${profileId}`)} className="btn-outline-navy flex items-center gap-2" data-testid="preview-book-btn">
          <BookOpen size={16} /> Preview the book
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-6">
        <div className="text-center mb-16">
          <div className="inline-block"><Monogram name={profile.name} size={64} /></div>
          <h1 className="text-4xl sm:text-5xl mt-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>{profile.name}'s stories</h1>
          <Ornament className="my-6" />
          <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
            {filled.length === 0 ? "Their book is waiting for the first word." : `${filled.length} stor${filled.length === 1 ? "y" : "ies"} captured so far.`}
          </p>
        </div>

        {filled.length === 0 ? (
          <div className="text-center py-20">
            <Ornament />
            <p className="mt-6 italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
              Their book begins as soon as you do.
            </p>
            <button onClick={() => navigate(`/app/profile/${profileId}`)} className="btn-primary mt-8" data-testid="start-first-story-btn">
              Begin a chapter
            </button>
          </div>
        ) : (
          <div className="space-y-16">
            {flatList.map((node, i) => {
              if (node.type === "chapter-header") {
                return (
                  <div key={`h-${i}`} className="text-center pt-6">
                    <p className="chapter-roman">{node.chapter.roman}</p>
                    <h2 className="text-3xl sm:text-4xl mt-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{node.chapter.title}</h2>
                    <p className="italic text-[#8C8278] mt-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>{node.chapter.subtitle}</p>
                    <Ornament className="my-6" />
                  </div>
                );
              }
              if (node.type === "quote") {
                return (
                  <div key={`q-${i}`} className="text-center py-10 px-6" style={{ background: "#F7F0E0", borderRadius: 4 }} data-testid={`feed-quote-${i}`}>
                    <p className="italic text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem", lineHeight: 1.55 }}>
                      "{node.quote.quote}"
                    </p>
                    <p className="label-ui text-[10px] text-[#B8973A] mt-4">{node.quote.author}</p>
                  </div>
                );
              }
              const s = node.story; const ch = node.chapter;
              const isEditing = editingId === s.story_id;
              return (
                <article key={s.story_id} className="bg-white p-8 sm:p-10 hairline-cream" style={{ borderRadius: 4 }} data-testid={`story-${s.story_id}`}>
                  {s.photo_path && <PhotoImg path={s.photo_path} />}
                  <p className="prompt-italic mb-6">"{ch.prompts[s.prompt_index]}"</p>
                  {isEditing ? (
                    <div data-testid={`edit-story-form-${s.story_id}`}>
                      <label htmlFor={`edit-${s.story_id}`} className="block mb-2 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: 500 }}>
                        Edit this response
                      </label>
                      <textarea
                        id={`edit-${s.story_id}`}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={8}
                        className="w-full px-4 py-3 hairline-cream bg-[#FAF7F2]"
                        style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.7 }}
                        data-testid={`edit-textarea-${s.story_id}`}
                      />
                      {editError && <p className="mt-2 text-red-700 text-sm" data-testid={`edit-error-${s.story_id}`}>{editError}</p>}
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => saveEdit(s.story_id)}
                          disabled={editSaving}
                          className="btn-primary flex items-center gap-2"
                          style={{ minHeight: 44 }}
                          data-testid={`edit-save-${s.story_id}`}
                        >
                          {editSaving ? "Saving…" : "Save changes"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="btn-outline-navy flex items-center gap-2"
                          style={{ minHeight: 44 }}
                          data-testid={`edit-cancel-${s.story_id}`}
                        >
                          <X size={14} /> Cancel
                        </button>
                        <p className="text-sm text-[#8C8278] italic self-center" style={{ fontFamily: "Georgia, serif" }}>
                          The voice recording is kept as-is.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="story-text whitespace-pre-wrap">{s.answer_text}</p>
                      <button
                        type="button"
                        onClick={() => beginEdit(s)}
                        className="mt-3 inline-flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#B8973A] transition-colors"
                        style={{ minHeight: 44 }}
                        aria-label="Edit this response"
                        data-testid={`edit-story-btn-${s.story_id}`}
                      >
                        <Pencil size={13} /> Edit this response
                      </button>
                    </>
                  )}
                  {s.audio_path && (
                    <div className="mt-6">
                      <AudioPlayer path={s.audio_path} label="Play the original recording" />
                    </div>
                  )}
                  {s.followup_question && s.followup_answer && (
                    <>
                      <div className="my-8 flex items-center gap-4">
                        <div className="flex-1 h-px bg-[#F0EAE0]" />
                        <span className="text-[#B8973A]">✦</span>
                        <div className="flex-1 h-px bg-[#F0EAE0]" />
                      </div>
                      <p className="prompt-italic mb-4">"{s.followup_question}"</p>
                      <p className="story-text whitespace-pre-wrap">{s.followup_answer}</p>
                    </>
                  )}
                  <div className="mt-8 pt-6 border-t hairline-cream flex justify-end">
                    <button onClick={() => setShareStory({ story: s, chapter: ch })} className="btn-outline-navy flex items-center gap-2" data-testid={`share-story-${s.story_id}`}>
                      <Share2 size={16} /> Share this story
                    </button>
                  </div>
                  <StoryReactions storyId={s.story_id} storytellerName={profile.name} />
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* SHARE MODAL */}
      {shareStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(17,29,51,0.7)" }} onClick={() => setShareStory(null)} data-testid="share-modal">
          <div className="max-w-xl w-full bg-[#FAF7F2] p-8 sm:p-10 max-h-[85vh] overflow-y-auto" style={{ borderRadius: 4 }} onClick={(e) => e.stopPropagation()}>
            <p className="label-ui text-[11px] text-[#B8973A] mb-3">Share this story</p>
            <h3 className="text-2xl mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>{shareStory.chapter.title}</h3>
            <div className="bg-white p-6 mb-6 hairline-cream max-h-[40vh] overflow-y-auto" style={{ borderRadius: 4 }}>
              <p className="prompt-italic mb-4">"{shareStory.chapter.prompts[shareStory.story.prompt_index]}"</p>
              <p className="story-text whitespace-pre-wrap">{shareStory.story.answer_text}</p>
              <p className="label-ui text-[10px] text-[#B8973A] mt-6">— {profile.name}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={copyShare} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="share-copy-btn">
                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy story text"}
              </button>
              <button onClick={emailShare} className="btn-outline-navy flex-1 flex items-center justify-center gap-2" data-testid="share-email-btn">
                <Mail size={16} /> Send by email
              </button>
            </div>
            <button onClick={() => setShareStory(null)} className="w-full mt-4 label-ui text-[10px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="share-close-btn">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
